import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmt, fmtFull } from './arrShared';
import { DevOverlay } from '../../components/DevOverlay/DevOverlay';

const EMPTY_ARRAY = [];
const PRODUCT_COLORS = ['#1e5fa8', '#15803d', '#d97706', '#6d28d9', '#0f766e', '#b91c1c', '#ea580c', '#64748b'];
const EVENT_COLORS = {
  New: '#15803d',
  Upsell: '#22c55e',
  Renewal: '#d97706',
  Churn: '#b91c1c',
  Downsell: '#ea580c',
  DOWNSELL: '#ea580c',
};
const EVENT_ICONS = {
  New: '🆕',
  Upsell: '⬆️',
  Renewal: '🔄',
  Churn: '❌',
  Downsell: '⬇️',
  DOWNSELL: '⬇️',
};
const eventTypeLabel = (type) => (type === 'Upsell' ? 'Expansion' : type);

function customerName(row) {
  return row.end_user || row.customer || row.bill_to || row.contract_name || '';
}

function arrMap(row) {
  return row.arr || row.monthly_arr || {};
}

function changesMap(row) {
  return row.changes || row.monthly_changes || {};
}

function previousCalendarMonth(month) {
  if (!month) return '';
  const [yearValue, monthValue] = month.split('-').map(Number);
  if (!yearValue || !monthValue) return '';
  const date = new Date(Date.UTC(yearValue, monthValue - 2, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function moneyOrDash(value) {
  return Number(value || 0) ? fmtFull(value) : '-';
}

function signedMoney(value) {
  const n = Number(value || 0);
  if (!n) return '-';
  return `${n > 0 ? '+' : '-'}${fmtFull(Math.abs(n))}`;
}

// Whole-dollar formatting for the top KPI tiles, matching the reference
// tool's fF() (always rounds to whole dollars, never shows cents).
function money0(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value || 0));
}

function signedMoney0(value) {
  const n = Number(value || 0);
  return `${n >= 0 ? '+' : '-'}${money0(Math.abs(n))}`;
}

function ChartCard({ title, sub, children, className = '' }) {
  return (
    <div className={`arr-c360-card ${className}`}>
      <h4>{title}</h4>
      {sub && <div className="arr-c360-card-sub">{sub}</div>}
      {children}
    </div>
  );
}

function SimpleLineChart({ data, color = '#1e5fa8', height = 240 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  if (!data.length) return <div className="arr-c360-chart-empty" style={{ height }}>No chart data</div>;
  const width = 760;
  const pad = { l: 58, r: 18, t: 22, b: 38 };
  const vals = data.map(row => Number(row.value || 0));
  const min = Math.min(0, ...vals);
  const max = Math.max(1, ...vals);
  const range = max - min || 1;
  const xFor = index => pad.l + (index / Math.max(data.length - 1, 1)) * (width - pad.l - pad.r);
  const yFor = value => pad.t + (height - pad.t - pad.b) - ((Number(value || 0) - min) / range) * (height - pad.t - pad.b);
  const points = data.map((row, index) => `${xFor(index).toFixed(1)},${yFor(row.value).toFixed(1)}`).join(' ');
  const area = `${pad.l},${height - pad.b} ${points} ${width - pad.r},${height - pad.b}`;
  const ticks = Array.from({ length: 5 }, (_, index) => min + ((range / 4) * index));
  const colStep = (width - pad.l - pad.r) / Math.max(1, data.length - 1);

  // Reference's Chart.js tooltip shows on hover anywhere near a point, not
  // just when the cursor lands exactly on a ~2px dot — matched here with
  // invisible per-month hit zones plus a floating SVG tooltip, the same
  // pattern already used by MultiTrendChart.
  const tooltipW = 150;
  const tooltipH = 44;
  const hoverX = hoverIdx != null ? xFor(hoverIdx) : null;
  const tooltipX = hoverX != null ? Math.min(Math.max(hoverX + 10, pad.l), width - tooltipW - 6) : 0;
  const tooltipY = pad.t + 6;

  return (
    <svg className="arr-c360-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" onMouseLeave={() => setHoverIdx(null)}>
      <defs>
        <linearGradient id={`c360Line${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".18" />
          <stop offset="100%" stopColor={color} stopOpacity=".02" />
        </linearGradient>
      </defs>
      {ticks.map((tick, index) => {
        const y = yFor(tick);
        return (
          <g key={index}>
            <line x1={pad.l} x2={width - pad.r} y1={y} y2={y} stroke="rgba(0,0,0,.05)" />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#8898aa">{fmt(tick)}</text>
          </g>
        );
      })}
      <polygon points={area} fill={`url(#c360Line${color.replace('#', '')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      {hoverIdx != null && (
        <line x1={hoverX} x2={hoverX} y1={pad.t} y2={height - pad.b} stroke="rgba(0,0,0,.15)" strokeDasharray="3 3" />
      )}
      {data.map((row, index) => (
        <circle
          key={row.month}
          cx={xFor(index)}
          cy={yFor(row.value)}
          r={index === data.length - 1 || index === hoverIdx ? 4.5 : 2.4}
          fill={color}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {data.map((row, index) => {
        if (data.length > 16 && index % Math.ceil(data.length / 10) !== 0 && index !== data.length - 1) return null;
        return <text key={`m-${row.month}`} x={xFor(index)} y={height - 12} textAnchor="middle" fontSize="10" fill="#8898aa">{row.month}</text>;
      })}
      {/* Invisible per-month hit zones — wider than the tiny point dots so
          hovering anywhere in a month's column reveals its tooltip. */}
      {data.map((row, index) => (
        <rect key={`hit-${row.month}`} x={xFor(index) - colStep / 2} y={pad.t} width={colStep} height={height - pad.t - pad.b}
          fill="transparent"
          onMouseEnter={() => setHoverIdx(index)}
          onMouseMove={() => setHoverIdx(index)} />
      ))}
      {hoverIdx != null && (
        <g pointerEvents="none">
          <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="8" fill="rgba(31,31,31,.92)" />
          <text x={tooltipX + 12} y={tooltipY + 20} fill="#fff" fontSize="12.5" fontWeight="800" fontFamily="Inter, system-ui, sans-serif">
            {data[hoverIdx].month}
          </text>
          <text x={tooltipX + 12} y={tooltipY + 36} fill="#fff" fontSize="11" fontFamily="Inter, system-ui, sans-serif">
            ARR: {fmtFull(data[hoverIdx].value)}
          </text>
        </g>
      )}
    </svg>
  );
}

function MultiLineChart({ months, series, height = 240 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const visible = series.filter(item => item.values.some(value => Number(value) > 0)).slice(0, 6);
  if (!visible.length) return <div className="arr-c360-chart-empty" style={{ height }}>No product trend data</div>;
  const width = 760;
  const pad = { l: 58, r: 18, t: 22, b: 42 };
  const vals = visible.flatMap(item => item.values.map(value => Number(value || 0)));
  const max = Math.max(1, ...vals);
  const xFor = index => pad.l + (index / Math.max(months.length - 1, 1)) * (width - pad.l - pad.r);
  const yFor = value => pad.t + (height - pad.t - pad.b) - (Number(value || 0) / max) * (height - pad.t - pad.b);
  const ticks = Array.from({ length: 5 }, (_, index) => (max / 4) * index);
  const baseY = height - pad.b;
  const colStep = (width - pad.l - pad.r) / Math.max(1, months.length - 1);

  // Matches the reference's Chart.js tooltip (hover anywhere near a month
  // shows every visible product's value that month), same hit-zone +
  // floating-tooltip pattern used by the other charts on this tab.
  const tooltipW = 168;
  const tooltipH = 30 + visible.length * 18 + 10;
  const hoverX = hoverIdx != null ? xFor(hoverIdx) : null;
  const tooltipX = hoverX != null ? Math.min(Math.max(hoverX + 10, pad.l), width - tooltipW - 6) : 0;
  const tooltipY = pad.t + 6;

  return (
    <div className="arr-c360-multi-wrap">
      <svg className="arr-c360-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" onMouseLeave={() => setHoverIdx(null)}>
        {ticks.map((tick, index) => {
          const y = yFor(tick);
          return (
            <g key={index}>
              <line x1={pad.l} x2={width - pad.r} y1={y} y2={y} stroke="rgba(0,0,0,.05)" />
              <text x={pad.l - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#8898aa">{fmt(tick)}</text>
            </g>
          );
        })}
        {visible.map((item, itemIndex) => {
          const color = PRODUCT_COLORS[itemIndex % PRODUCT_COLORS.length];
          const points = item.values.map((value, index) => `${xFor(index).toFixed(1)},${yFor(value).toFixed(1)}`).join(' ');
          return (
            <g key={item.label}>
              <polyline points={points} fill="none" stroke={color} strokeWidth="2.2" vectorEffect="non-scaling-stroke" />
              {hoverIdx != null && (
                <circle cx={xFor(hoverIdx)} cy={yFor(item.values[hoverIdx])} r="3.5" fill={color} vectorEffect="non-scaling-stroke" />
              )}
            </g>
          );
        })}
        {hoverIdx != null && (
          <line x1={hoverX} x2={hoverX} y1={pad.t} y2={baseY} stroke="rgba(0,0,0,.15)" strokeDasharray="3 3" />
        )}
        {months.map((month, index) => {
          if (months.length > 16 && index % Math.ceil(months.length / 10) !== 0 && index !== months.length - 1) return null;
          return <text key={month} x={xFor(index)} y={height - 14} textAnchor="middle" fontSize="10" fill="#8898aa">{month}</text>;
        })}
        {/* Invisible per-month hit zones — wider than the thin polylines so
            hovering anywhere in a month's column shows that month's values. */}
        {months.map((month, index) => (
          <rect key={`hit-${month}`} x={xFor(index) - colStep / 2} y={pad.t} width={colStep} height={baseY - pad.t}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(index)}
            onMouseMove={() => setHoverIdx(index)} />
        ))}
        {hoverIdx != null && (
          <g pointerEvents="none">
            <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="8" fill="rgba(31,31,31,.92)" />
            <text x={tooltipX + 12} y={tooltipY + 20} fill="#fff" fontSize="12.5" fontWeight="800" fontFamily="Inter, system-ui, sans-serif">
              {months[hoverIdx]}
            </text>
            {visible.map((item, itemIndex) => (
              <g key={item.label}>
                <rect x={tooltipX + 12} y={tooltipY + 30 + itemIndex * 18} width="8" height="8" rx="2" fill={PRODUCT_COLORS[itemIndex % PRODUCT_COLORS.length]} />
                <text x={tooltipX + 26} y={tooltipY + 38 + itemIndex * 18} fill="#fff" fontSize="10.5" fontFamily="Inter, system-ui, sans-serif">
                  {item.label}: {fmtFull(item.values[hoverIdx] || 0)}
                </text>
              </g>
            ))}
          </g>
        )}
      </svg>
      <div className="arr-c360-legend">
        {visible.map((item, index) => (
          <span key={item.label}><i style={{ background: PRODUCT_COLORS[index % PRODUCT_COLORS.length] }} />{item.label}</span>
        ))}
      </div>
    </div>
  );
}

// Donut-wedge path: angleDeg 0 = 12 o'clock, increasing clockwise. Same
// helpers as ARRDashboard.js's BUChart/SubProductDonut.
function polarPoint(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function donutSlicePath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const outerStart = polarPoint(cx, cy, rOuter, startAngle);
  const outerEnd = polarPoint(cx, cy, rOuter, endAngle);
  const innerEnd = polarPoint(cx, cy, rInner, endAngle);
  const innerStart = polarPoint(cx, cy, rInner, startAngle);
  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

// Rebuilt as an SVG-wedge donut matching the same pattern already used by
// ARRDashboard.js's BUChart/SubProductDonut (pop-out hover, center label
// swaps to the hovered segment, matching legend buttons) instead of the
// older stroke-dasharray-circle technique with a flat opacity hover.
function DonutChart({ items }) {
  const rows = items
    .filter((item) => Number(item.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
  const total = rows.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const [hoverIdx, setHoverIdx] = useState(null);

  if (!rows.length || !total) return <div className="arr-c360-chart-empty">No product data</div>;

  let cumAngle = 0;
  const segments = rows.map((row, i) => {
    const pctVal = (Number(row.value || 0) / total) * 100;
    const startAngle = cumAngle;
    cumAngle += (pctVal / 100) * 360;
    return { row, i, startAngle, endAngle: cumAngle, midAngle: (startAngle + cumAngle) / 2, pctVal };
  });

  const CX = 100;
  const CY = 100;
  const R_OUTER = 82;
  const R_INNER = 50;
  const POP = 8;
  const active = hoverIdx != null ? segments[hoverIdx] : null;

  return (
    <div className="arr-prod-donut-layout" onMouseLeave={() => setHoverIdx(null)}>
      <div className="arr-bu-donut-stage">
        <svg viewBox="0 0 200 200" className="arr-bu-donut-svg">
          {segments.map((seg) => {
            const isActive = hoverIdx === seg.i;
            const midRad = ((seg.midAngle - 90) * Math.PI) / 180;
            const dx = isActive ? Math.cos(midRad) * POP : 0;
            const dy = isActive ? Math.sin(midRad) * POP : 0;
            const color = PRODUCT_COLORS[seg.i % PRODUCT_COLORS.length];
            // A single 100%-share segment has identical start/end points,
            // which SVG renders as an invisible zero-length arc — split it
            // into two 180° arcs instead so a lone full-share category still
            // draws a complete ring (same fix as the Pipeline BU donut).
            const isFullCircle = seg.endAngle - seg.startAngle >= 359.99;
            const wedges = isFullCircle
              ? [[seg.startAngle, seg.startAngle + 180], [seg.startAngle + 180, seg.endAngle]]
              : [[seg.startAngle, seg.endAngle]];
            return (
              <g key={seg.row.label}>
                {wedges.map(([a0, a1], wi) => {
                  const path = donutSlicePath(CX, CY, R_OUTER, R_INNER, a0, a1);
                  return (
                    <g key={wi}>
                      {/* Visual wedge only — the pop-out transform moves this, so
                          it must not carry the hover handlers or the cursor ends
                          up hovering empty space once it shifts away. */}
                      <path
                        d={path}
                        className={`arr-bu-donut-slice${isActive ? ' active' : ''}`}
                        fill={color}
                        style={{
                          stroke: color,
                          transform: `translate(${dx}px, ${dy}px)`,
                          opacity: hoverIdx == null || isActive ? 1 : 0.55,
                          pointerEvents: 'none',
                        }}
                      />
                      {/* Stable hit target — stays at the untranslated slice
                          geometry so hovering never moves out from under the cursor. */}
                      <path
                        d={path}
                        fill="transparent"
                        tabIndex={0}
                        role="button"
                        onMouseEnter={() => setHoverIdx(seg.i)}
                        onFocus={() => setHoverIdx(seg.i)}
                        aria-label={`${seg.row.label} ${fmtFull(seg.row.value)}`}
                      >
                        <title>{`${seg.row.label}: ${fmtFull(seg.row.value)} (${seg.pctVal.toFixed(1)}%)`}</title>
                      </path>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
        <div className="arr-bu-donut-center">
          {active ? (
            <>
              <div className="arr-bu-donut-center-label">{active.row.label}</div>
              <div className="arr-bu-donut-center-val" style={{ color: PRODUCT_COLORS[active.i % PRODUCT_COLORS.length] }}>{fmt(active.row.value)}</div>
              <div className="arr-bu-donut-center-pct">{active.pctVal.toFixed(1)}% of total</div>
            </>
          ) : (
            <>
              <div className="arr-bu-donut-center-label">Total</div>
              <div className="arr-bu-donut-center-val">{fmt(total)}</div>
            </>
          )}
        </div>
      </div>
      <div className="arr-prod-legend">
        {segments.map((seg) => (
          <button
            key={seg.row.label}
            type="button"
            className={hoverIdx === seg.i ? 'active' : ''}
            onMouseEnter={() => setHoverIdx(seg.i)}
            onFocus={() => setHoverIdx(seg.i)}
          >
            <i style={{ background: PRODUCT_COLORS[seg.i % PRODUCT_COLORS.length] }} />{seg.row.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Matches the reference's c360OpenModal — a dark navy-gradient-header modal
// portaled to <body> (so it always renders above the navbar/other stacking
// contexts, same fix as QuotaVsAOP's popup).
function C360KpiModal({ modal, onClose }) {
  if (!modal || typeof document === 'undefined') return null;
  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9999, overflowY: 'auto' }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, maxWidth: 680, width: '94%', margin: '60px auto', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,.25)' }}
      >
        <div style={{ background: 'linear-gradient(135deg,#0f2d52,#1a3f6e)', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>{modal.title}</div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', fontSize: 18, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer' }}
          >×</button>
        </div>
        <div style={{ padding: '20px 22px', maxHeight: '60vh', overflowY: 'auto' }}>{modal.body}</div>
      </div>
    </div>,
    document.body
  );
}

function Customer360Tab({ data, focusCustomer }) {
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [kpiModal, setKpiModal] = useState(null);
  const records = data.records || EMPTY_ARRAY;

  // Jump straight to a customer's detail when arriving via a treemap click
  // elsewhere in the dashboard (e.g. ARR Concentration Treemap), same as
  // clicking a tile in the reference tool selects it in the Customer 360 tab.
  // Keyed on token (not just name) so clicking the same tile twice in a row
  // still re-selects it even if the dropdown was changed in between.
  useEffect(() => {
    if (focusCustomer?.name) setSelectedCustomer(focusCustomer.name);
  }, [focusCustomer]);

  const base = useMemo(() => {
    // The reference uses the import's complete, globally-active month list.
    const monthSet = new Set(data.period?.months || data.all_months || []);
    records.forEach(row => {
      Object.keys(arrMap(row)).forEach(month => monthSet.add(month));
      Object.keys(changesMap(row)).forEach(month => monthSet.add(month));
    });
    const allMonths = [...monthSet].filter(Boolean).sort();
    // Dropdown amounts always use the import's global latest month, regardless
    // of the currently selected reporting period.
    const latest = data.period?.latest_month || data.latest_month || data.kpis?.latest_month || allMonths.at(-1) || '';
    const activeMonths = allMonths.filter(month => records.some(row => Number(arrMap(row)[month] || 0) > 0));

    const customerStats = {};
    records.forEach(row => {
      const name = customerName(row);
      if (!name) return;
      const monthly = arrMap(row);
      if (!customerStats[name]) customerStats[name] = { customer: name, arr: 0, peak: 0 };
      customerStats[name].arr += Number(monthly[latest] || 0);
      Object.values(monthly).forEach(value => {
        customerStats[name].peak = Math.max(customerStats[name].peak, Number(value || 0));
      });
    });

    const customers = Object.values(customerStats)
      .filter(customer => customer.peak > 0)
      .sort((a, b) => Number(b.arr || 0) - Number(a.arr || 0) || Number(b.peak || 0) - Number(a.peak || 0));

    return { allMonths, activeMonths, latest, customers };
  }, [data, records]);

  const customerDetail = useMemo(() => {
    if (!selectedCustomer) return null;
    const deals = records.filter(row => customerName(row) === selectedCustomer);
    if (!deals.length) return null;

    // Customer detail is evaluated at the filter bar's To month. This is the
    // key distinction between the dropdown amount and the Customer 360 KPIs.
    const latest = data.period?.to || base.latest;
    const prevM = previousCalendarMonth(latest);
    const arrByMonth = {};
    deals.forEach(row => {
      Object.entries(arrMap(row)).forEach(([month, value]) => {
        arrByMonth[month] = (arrByMonth[month] || 0) + Number(value || 0);
      });
    });

    const activeMonths = Object.keys(arrByMonth).filter(month => arrByMonth[month] > 0).sort();
    const firstM = activeMonths[0] || latest;
    const lastM = activeMonths.at(-1) || latest;
    const firstIdx = Math.max(0, base.allMonths.indexOf(firstM));
    const trimMonths = base.allMonths.slice(firstIdx);
    const currARR = Number(arrByMonth[latest] || arrByMonth[lastM] || 0);
    const prevARR = Number(arrByMonth[prevM] || 0);
    const mom = currARR - prevARR;
    // Period-scoped totals (New/Upsell/Churn/Downsell, NRR%) track the
    // dashboard's live period filter (YTD by default via data.period.from),
    // exactly like every other card. Verified 2026-07-21 by executing the
    // reference tool's own getPeriodRange/buildC360 logic directly against
    // its embedded dataset for a fresh YTD-2026 load: Concentrix and Lowes
    // both come out to $0 new+upsell / 100% NRR, matching this app. (An
    // earlier hardcoded 2025-01 pin was tried to match a one-off Concentrix
    // reading, but Lowes disproves it — Lowes has real Sep/Dec-2025 upsell
    // events that a 2025-01 window would have to include, yet the reference
    // shows $0 for Lowes, so that earlier reading was a stale-filter
    // artifact in that specific browser session, not the tool's true
    // behavior.)
    const periodFrom = data.period?.from || '';
    const periodMonths = base.activeMonths.filter(month => (!periodFrom || month >= periodFrom) && month <= latest);
    const tenureMonths = base.activeMonths.indexOf(latest) - base.activeMonths.indexOf(firstM);

    let totalNew = 0;
    let totalUpsell = 0;
    let totalRenewal = 0;
    let totalChurn = 0;
    let totalDownsell = 0;
    const productCurrent = {};
    const eventRows = [];
    const repData = {};
    const productTrendMap = {};

    deals.forEach(row => {
      const referenceProduct = row.sub_product || row.product_l2 || '';
      const rep = row.sales_person || row.sales_rep || '-';
      const monthly = arrMap(row);
      const changes = changesMap(row);

      // The HTML only includes rows with a real sub-product in its product
      // badge, donut, and product trend (and never falls back to lastM here).
      if (referenceProduct) {
        productCurrent[referenceProduct] = (productCurrent[referenceProduct] || 0) + Number(monthly[latest] || 0);
        if (!productTrendMap[referenceProduct]) productTrendMap[referenceProduct] = {};
        trimMonths.forEach(month => {
          productTrendMap[referenceProduct][month] = (productTrendMap[referenceProduct][month] || 0) + Number(monthly[month] || 0);
        });
      }

      if (!repData[rep]) repData[rep] = { arr: 0, newWon: 0, upsell: 0, products: new Set() };
      repData[rep].arr += Number(monthly[latest] || 0);
      if (referenceProduct) repData[rep].products.add(referenceProduct);

      periodMonths.forEach(month => {
        const change = changes[month] || {};
        const values = {
          New: Number(change.New || 0),
          Upsell: Number(change.Upsell || 0),
          Renewal: Number(change.Renewal || 0),
          Churn: Number(change.Churn || 0),
          Downsell: Number(change.Downsell || change.DOWNSELL || 0),
        };
        totalNew += Math.max(0, values.New);
        totalUpsell += Math.max(0, values.Upsell);
        totalRenewal += Math.max(0, values.Renewal);
        totalChurn += Math.abs(Math.min(0, values.Churn));
        totalDownsell += Math.abs(Math.min(0, values.Downsell));
        repData[rep].newWon += Math.max(0, values.New);
        repData[rep].upsell += Math.max(0, values.Upsell);

        // Unlike KPI totals, the timeline renders every raw event key exactly
        // as stored in the reference data.
        Object.entries(change).forEach(([type, rawValue]) => {
          const value = Number(rawValue || 0);
          if (!value) return;
          eventRows.push({
            month,
            type,
            amount: value,
            product: row.sub_product || row.product_l2 || '-',
            rep,
            arrAfter: arrByMonth[month] || 0,
          });
        });
      });
    });

    eventRows.sort((a, b) => a.month.localeCompare(b.month));

    // Product count badge counts every sub-product this customer has ever had
    // a deal line for (matches the reference's prodSet key count), not just
    // ones with current-month ARR > 0 — productMix below stays value-filtered
    // since it drives the ARR-by-product breakdown/chart.
    const productCount = Object.keys(productCurrent).length;
    const productMix = Object.entries(productCurrent)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    // Reference ranks/picks the top 6 products by CURRENT (latest-month) ARR
    // — the same ranking as productMix — not by lifetime cumulative total,
    // which could surface an old, now-dead product ahead of a smaller but
    // currently-active one.
    const productTrend = Object.entries(productTrendMap)
      .map(([label, monthMap]) => ({
        label,
        total: Object.values(monthMap).reduce((sum, value) => sum + Number(value || 0), 0),
        values: trimMonths.map(month => Number(monthMap[month] || 0)),
      }))
      .sort((a, b) => (productCurrent[b.label] || 0) - (productCurrent[a.label] || 0));

    const repHistory = Object.entries(repData)
      .map(([rep, row]) => ({
        rep,
        arr: row.arr,
        newWon: row.newWon,
        upsell: row.upsell,
        products: [...row.products].filter(Boolean).join(', '),
      }))
      .sort((a, b) => b.arr - a.arr);

    const info = deals[0] || {};
    const region = info.region || info.business_unit || '-';
    const nrr = prevARR > 0 ? ((prevARR - totalChurn - totalDownsell + totalUpsell) / prevARR) * 100 : 0;
    const lastThree = activeMonths.slice(-3).map(month => arrByMonth[month] || 0);
    const declining = lastThree.length >= 2 && lastThree.at(-1) < lastThree[0];
    const risk = currARR === 0
      ? { label: '🔴 Churned', color: '#b91c1c', bg: '#fee2e2' }
      : declining
        ? { label: '🟠 Declining', color: '#f97316', bg: '#fff7ed' }
        : { label: '🟢 Healthy', color: '#15803d', bg: '#f0fdf4' };

    let insight = null;
    if (currARR === 0) {
      insight = { tone: 'danger', text: `⚠️ ${selectedCustomer} has churned - last active ARR was ${money0(Math.max(...activeMonths.map(month => arrByMonth[month] || 0), 0))} in ${lastM}` };
    } else if (declining) {
      const dropPct = lastThree[0] > 0 ? ((lastThree[0] - lastThree.at(-1)) / lastThree[0]) * 100 : 0;
      insight = { tone: 'warn', text: `🟠 ${selectedCustomer} ARR has declined ${dropPct.toFixed(1)}% over the last 3 months - may require attention` };
    } else if (nrr > 110) {
      insight = { tone: 'good', text: `🌟 ${selectedCustomer} is a top expander - NRR ${nrr.toFixed(1)}% with ${money0(totalUpsell)} expansion in period` };
    } else if (totalChurn + totalDownsell > 0) {
      insight = { tone: 'warn', text: `⚠️ ${selectedCustomer} had ${money0(totalChurn + totalDownsell)} in churn/downsell this period` };
    }

    return {
      deals,
      region,
      firstM,
      lastM,
      latest,
      prevM,
      currARR,
      prevARR,
      mom,
      totalNew,
      totalUpsell,
      totalRenewal,
      totalChurn,
      totalDownsell,
      tenureMonths,
      nrr,
      nrrColor: nrr >= 100 ? '#15803d' : nrr >= 80 ? '#d97706' : '#b91c1c',
      productCount,
      productMix,
      productTrend,
      trimMonths,
      trend: trimMonths.map(month => ({ month, value: arrByMonth[month] || 0 })),
      events: eventRows,
      repHistory,
      risk,
      insight,
    };
  }, [selectedCustomer, records, base, data.period?.from, data.period?.to]);

  // Matches the reference's c360KpiClick exactly: only "Closing ARR" and
  // "NRR %" get a real drill-down table — the other three cards (MoM Change,
  // Period New Won, Tenure) are wired up in the reference but fall through
  // to a generic placeholder, since it never built specific breakdowns for
  // them either. Replicated as-is rather than inventing richer content.
  const openKpiModal = (label) => {
    if (!customerDetail) return;
    let body;
    if (label === 'Closing ARR') {
      body = (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {customerDetail.productMix.map((item) => (
              <tr key={item.label} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '9px 12px', fontWeight: 600 }}>{item.label}</td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700, color: '#1e5fa8' }}>{money0(item.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (label === 'NRR %') {
      // Reference recomputes Upsell/Churn/Downsell for this table from just
      // the latest month's change record — not the period-scoped totals used
      // for the NRR% tile itself, so these rows won't always foot to it.
      let periodUp = 0;
      let periodChurn = 0;
      let periodDownsell = 0;
      customerDetail.deals.forEach((row) => {
        const change = changesMap(row)[customerDetail.latest] || {};
        periodUp += Number(change.Upsell || 0);
        periodChurn += Math.abs(Number(change.Churn || 0));
        periodDownsell += Math.abs(Number(change.Downsell || change.DOWNSELL || 0));
      });
      const rows = [
        ['Starting ARR', money0(customerDetail.prevARR), '#718096'],
        ['+ Expansion', money0(periodUp), '#15803d'],
        ['- Churn', money0(-periodChurn), '#b91c1c'],
        ['- Downsell', money0(-periodDownsell), '#ea580c'],
        ['= Ending ARR', money0(customerDetail.currARR), '#1e5fa8'],
      ];
      body = (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {rows.map(([rowLabel, value, color]) => (
              <tr key={rowLabel} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '9px 12px', fontWeight: 600 }}>{rowLabel}</td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: '"Courier New", monospace', fontWeight: 700, color }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      body = <div style={{ padding: 20, color: '#718096', textAlign: 'center' }}>Select a specific metric for a drill-down</div>;
    }
    setKpiModal({ title: `${label} — ${selectedCustomer}`, body });
  };

  return (
    <>
      <C360KpiModal modal={kpiModal} onClose={() => setKpiModal(null)} />
      <DevOverlay name="C360 Selector">
        <div className="arr-c360-selector">
          <div className="arr-c360-selector-inner">
            <div className="arr-c360-selector-ctrl">
              <label className="arr-c360-select-label">Select Customer</label>
              <select className="arr-c360-select" value={selectedCustomer} onChange={event => setSelectedCustomer(event.target.value)}>
                <option value="">- Choose a customer -</option>
                {base.customers.map(customer => (
                  <option key={customer.customer} value={customer.customer}>
                    {customer.customer} ({customer.arr > 0 ? fmt(customer.arr) : 'historical'})
                  </option>
                ))}
              </select>
            </div>
            {customerDetail && (
              <div className="arr-c360-badges">
                <span className="arr-tag">{customerDetail.region}</span>
              </div>
            )}
          </div>
        </div>
      </DevOverlay>

      {!selectedCustomer && (
        <div className="arr-c360-empty">
          <div className="arr-c360-empty-icon">👁️</div>
          <h3>Select a customer above</h3>
          <p>Full ARR history, deal events, product breakdown and rep activity</p>
        </div>
      )}

      {selectedCustomer && !customerDetail && (
        <div className="arr-c360-empty">
          <h3>No data for {selectedCustomer}</h3>
        </div>
      )}

      {customerDetail && (
        <div className="arr-c360-content">
          <div className="arr-c360-kpis">
            {[
              { label: 'Closing ARR', value: money0(customerDetail.currARR), sub: `as of ${customerDetail.latest}`, color: '#1e5fa8', tooltip: 'View ARR breakdown by product' },
              { label: 'MoM Change', value: signedMoney0(customerDetail.mom), sub: `vs ${customerDetail.prevM || 'prior'}`, color: customerDetail.mom >= 0 ? '#15803d' : '#b91c1c', tooltip: 'Month-over-month ARR change' },
              { label: 'Period New Won', value: money0(customerDetail.totalNew + customerDetail.totalUpsell), sub: 'new + expansion in period', color: '#15803d', tooltip: 'Bookings breakdown for this period' },
              { label: 'Tenure', value: `${customerDetail.tenureMonths}m`, sub: `since ${customerDetail.firstM}`, color: '#6d28d9', tooltip: `Customer since ${customerDetail.firstM}` },
              { label: 'NRR %', value: `${customerDetail.nrr.toFixed(1)}%`, sub: 'net revenue retention', color: customerDetail.nrrColor, tooltip: 'NRR = (Base + Expansion - Churn - Downsell) / Base' },
            ].map(card => (
              <DevOverlay key={card.label} name={`C360 ${card.label}`}>
                <div
                  className="arr-c360-kpi"
                  style={{ borderTopColor: card.color, cursor: 'pointer' }}
                  title={card.tooltip}
                  onClick={() => openKpiModal(card.label)}
                >
                  <div className="arr-c360-kpi-click">🔍 Click</div>
                  <div className="arr-c360-kpi-label">{card.label}</div>
                  <div className="arr-c360-kpi-val" style={{ color: card.color }}>{card.value}</div>
                  <div className="arr-c360-kpi-sub">{card.sub}</div>
                </div>
              </DevOverlay>
            ))}
          </div>

          {customerDetail.insight && (
            <div className={`arr-c360-insight ${customerDetail.insight.tone}`}>
              {customerDetail.insight.text}
            </div>
          )}

          <div className="arr-c360-grid2">
            <DevOverlay name="C360 ARR History">
              <ChartCard
                title="ARR History - Full Lifetime"
                sub={`${customerDetail.trend.filter(row => row.value > 0).length} months of ARR history | ${customerDetail.firstM} -> ${customerDetail.lastM}`}
              >
                <SimpleLineChart data={customerDetail.trend} color="#1e5fa8" height={250} />
              </ChartCard>
            </DevOverlay>
            <DevOverlay name="C360 Product Mix">
              <ChartCard title="Product Mix" sub="Current ARR split by sub-product">
                <DonutChart items={customerDetail.productMix} />
              </ChartCard>
            </DevOverlay>
          </div>

          <DevOverlay name="C360 Events Timeline">
            <ChartCard title="📅 Deal Events Timeline" sub="Every New, Expansion, Renewal, Downsell, Churn event - chronological" className="arr-c360-card-spaced">
              <div className="arr-c360-table-scroll">
                <table className="arr-c360-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Event Type</th>
                      <th className="right">Amount</th>
                      <th>Product</th>
                      <th>Sales Rep</th>
                      <th className="right">ARR After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerDetail.events.map((event, index) => {
                      const color = EVENT_COLORS[event.type] || '#64748b';
                      return (
                        <tr key={`${event.month}-${event.type}-${event.product}-${index}`}>
                          <td><strong>{event.month}</strong></td>
                          <td><span className="arr-c360-event" style={{ background: `${color}18`, color }}>{EVENT_ICONS[event.type] || '•'} {eventTypeLabel(event.type)}</span></td>
                          <td className="right money" style={{ color }}>{signedMoney(event.amount)}</td>
                          <td>{event.product}</td>
                          <td><span className="arr-c360-link">{event.rep}</span></td>
                          <td className="right money">{fmtFull(event.arrAfter)}</td>
                        </tr>
                      );
                    })}
                    {!customerDetail.events.length && (
                      <tr><td colSpan={6} className="bm-empty-row">No deal events in selected period</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </DevOverlay>

          <div className="arr-c360-grid2">
            <DevOverlay name="C360 Product Trend">
              <ChartCard title="ARR by Product — Trend" sub="Monthly ARR per sub-product for this customer">
                <MultiLineChart months={customerDetail.trimMonths} series={customerDetail.productTrend} height={250} />
              </ChartCard>
            </DevOverlay>
            <DevOverlay name="C360 Sales Rep History">
              <ChartCard title="Sales Rep History" sub="ARR managed by each rep for this customer">
                <div className="arr-c360-table-scroll short">
                  <table className="arr-c360-table">
                    <thead>
                      <tr>
                        <th>Rep</th>
                        <th className="right">ARR Managed</th>
                        <th className="right">New Won</th>
                        <th className="right">Expansion</th>
                        <th>Products</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerDetail.repHistory.map((row, index) => {
                        const maxArr = Math.max(...customerDetail.repHistory.map(rep => rep.arr), 1);
                        return (
                          <tr key={row.rep}>
                            <td><span className="arr-c360-link">{row.rep}</span></td>
                            <td className="right">
                              <div className="arr-c360-rep-arr">
                                <span><i style={{ width: `${Math.round((row.arr / maxArr) * 100)}%`, background: PRODUCT_COLORS[index % PRODUCT_COLORS.length] }} /></span>
                                <strong style={{ color: PRODUCT_COLORS[index % PRODUCT_COLORS.length] }}>{fmtFull(row.arr)}</strong>
                              </div>
                            </td>
                            <td className="right money arr-up">{moneyOrDash(row.newWon)}</td>
                            <td className="right money arr-up">{moneyOrDash(row.upsell)}</td>
                            <td>{row.products || '-'}</td>
                          </tr>
                        );
                      })}
                      {!customerDetail.repHistory.length && (
                        <tr><td colSpan={5} className="bm-empty-row">No rep history available.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            </DevOverlay>
          </div>
        </div>
      )}
    </>
  );
}

export default Customer360Tab;
export { Customer360Tab };
